/* eslint-disable */
// @ts-nocheck

import { ChevronDown, Plus, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Column } from '@tanstack/react-table'

import useServerArea from '@/api/useServerArea'
import Modal from '@/components/modals/GenericModal'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { DocumentType } from '@/containers/billing/register/billingPortals/newRegisterWizard/NewRegisterWizard'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useBusinessPackagesQuery } from '@/hooks/business/useBusinessPackagesQuery'
import { Area } from '@/interfaces/area'

import NewArea from './NewArea'
import { useTableContext } from './TableContext'

export const HeaderActions = ({
  columns,
}: {
  columns: Column<Area, unknown>[]
}) => {
  // Use the context
  const hiddenColumns = columns.filter((column) => column.getCanHide())

  const { currentPage, pageSize, areaData, isLoading, refetch, setPageSize } =
    useTableContext()
  const { addArea, isFetching } = useServerArea()
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})
  const { businessPackages, isLoadingBusinessPackages } =
    useBusinessPackagesQuery({
      params: { businessId: businessData?.id },
      options: {
        enabled:
          !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
      },
    })

  const [isPrebillingWizardOpen, setIsPrebillingWizardOpen] = useState(false)
  const [isBillingWizardOpen, setIsBillingWizardOpen] = useState(false)
  const [openModal, setOpenModal] = useState<boolean>(false)

  const [isMobileView, setIsMobileView] = useState(false)

  // Check for mobile view on mount and window resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  const openWizard = (type: DocumentType) => {
    if (type === 'prefactura') {
      setIsPrebillingWizardOpen(true)
    } else {
      setIsBillingWizardOpen(true)
    }
  }

  const submitAction = (data: Record<string, string>) =>
    addArea(data, () => setOpenModal(false))

  // For mobile: stacked layout
  if (isMobileView) {
    return (
      <div className="flex-col gap-4 p-4">
        <div className="mb-4 flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              aria-label="Refrescar tabla"
              className="h-8 w-8"
              disabled={isLoading}
              onClick={refetch}
              size="icon"
              variant="ghost"
            >
              {isLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {hiddenColumns.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label="Mostrar columnas"
                    className="h-8 gap-1"
                    size="sm"
                    variant="outline"
                  >
                    <span>Columnas</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {columns
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          checked={column.getIsVisible()}
                          className="cursor-pointer"
                          key={column.id}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {typeof column.columnDef.header === 'string'
                            ? column.columnDef.header
                            : column.id}
                        </DropdownMenuCheckboxItem>
                      )
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="Mostrar columnas"
                  className="h-8 gap-1"
                  size="sm"
                  variant="outline"
                >
                  <span>Resultados por página: {pageSize}</span>
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Resultados por página</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => setPageSize(20)}
                >
                  <span>20</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => setPageSize(50)}
                >
                  <span>50</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => setPageSize(100)}
                >
                  <span>100</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="hover:cursor-pointer hover:bg-muted-foreground/10"
                  onClick={() => setPageSize(500)}
                >
                  <span>500</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="bg-muted-foregrohover:und hover:cursor/10-pointer"
                  onClick={() => setPageSize(1000)}
                >
                  <span>1000</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex w-full flex-col gap-2">
          <Button
            className="h-8 w-full gap-1"
            onClick={() => setOpenModal(true)}
            size="sm"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            <span>Nueva área</span>
          </Button>
          {openModal && (
            <Modal close={() => setOpenModal(false)} size="m" state={openModal}>
              <NewArea loading={isFetching} submitAction={submitAction} />
            </Modal>
          )}
        </div>
      </div>
    )
  }

  // For desktop: layout with creation buttons on left, pagination in middle, and utility buttons on right
  return (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-center gap-2">
        <Button
          className="h-8 gap-1"
          onClick={() => setOpenModal(true)}
          size="sm"
          variant="outline"
        >
          <Plus className="h-4 w-4" />
          <span>Nueva área</span>
        </Button>
        {openModal && (
          <Modal close={() => setOpenModal(false)} size="m" state={openModal}>
            <NewArea loading={isFetching} submitAction={submitAction} />
          </Modal>
        )}
      </div>

      {/* Middle - Pagination info */}
      <div className="flex items-center gap-2">
        <Button
          aria-label="Refrescar tabla"
          className="h-8 w-8"
          disabled={isLoading}
          onClick={refetch}
          size="icon"
          variant="ghost"
        >
          {isLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
        <span className="min-h-[20px] min-w-[300px] text-sm text-muted-foreground">
          {isLoading ? (
            <Skeleton className="h-4 w-[300px]" />
          ) : areaData?.totalItems ? (
            <>
              Mostrando {(currentPage - 1) * pageSize + 1} al{' '}
              {Math.min(currentPage * pageSize, areaData.totalItems)} de{' '}
              {areaData.totalItems} resultados
            </>
          ) : (
            'No hay resultados'
          )}
        </span>
      </div>

      {/* Right - Export and columns buttons */}
      <div className="flex items-center gap-2">
        {/* <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              aria-label="Exportar datos"
              disabled={isLoading}
            >
              <span>Exportar</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Exportar</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <BsFilePdf className="mr-2 h-4 w-4" />
              <span>Exportar a PDF</span>
            </DropdownMenuItem>
            <DropdownMenuItem>
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              <span>Exportar a Excel</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu> */}

        {hiddenColumns.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label="Mostrar columnas"
                className="h-8 gap-1"
                size="sm"
                variant="outline"
              >
                <span>Columnas</span>
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Mostrar columnas</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columns
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      checked={column.getIsVisible()}
                      className="cursor-pointer"
                      key={column.id}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {typeof column.columnDef.header === 'string'
                        ? column.columnDef.header
                        : column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label="Mostrar columnas"
              className="h-8 gap-1"
              size="sm"
              variant="outline"
            >
              <span>Resultados por página: {pageSize}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Resultados por página</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(20)}
            >
              <span>20</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(50)}
            >
              <span>50</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(100)}
            >
              <span>100</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(500)}
            >
              <span>500</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="hover:cursor-pointer hover:bg-muted-foreground/10"
              onClick={() => setPageSize(1000)}
            >
              <span>1000</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
